import { useEffect, useMemo, useState } from 'react'
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Card, Button, Title, Text, Icon } from '@nimbus-ds/components'
import { StatsIcon } from '@nimbus-ds/icons'
import {
  funnelService,
  engagedFromSegments,
  type CellSegments,
  type ProductsOriginsTimelineResponse,
  type ProductsOriginsTimelinePoint,
} from '@/api/funnel'

type Metric = 'sessions' | 'conversions' | 'conversion_rate' | 'revenue' | 'value_per_visit' | 'engagement_rate'
type Smoothing = 'raw' | 'ma3'
type Dimension = 'channel' | 'origin' | 'campaign' | 'device'

const METRIC_LABELS: Record<Metric, string> = {
  sessions: 'Sesiones',
  conversions: 'Conversiones',
  conversion_rate: '% Conv.',
  revenue: 'Ingresos',
  value_per_visit: '$/visita',
  engagement_rate: '% Engag.',
}

/** Rates and money format differently on the axis and in the tooltip. */
const RATE_METRICS = new Set<Metric>(['conversion_rate', 'engagement_rate'])
const MONEY_METRICS = new Set<Metric>(['revenue', 'value_per_visit'])
/**
 * Metrics for which "no data" is a GAP, not a zero. A count can honestly be 0 for a day;
 * a ratio over an empty (or unmeasured) denominator cannot, and drawing it as 0 invents a
 * collapse. These plot null and `connectNulls={false}` leaves the line broken.
 */
const NULLABLE_METRICS = new Set<Metric>(['conversion_rate', 'engagement_rate', 'value_per_visit'])

const SMOOTHING_LABELS: Record<Smoothing, string> = {
  raw: 'Diario',
  ma3: '3d avg',
}

const DIMENSION_LABELS: Record<Dimension, string> = {
  channel: 'Grupo',
  origin: 'Origen',
  campaign: 'Campaña',
  device: 'Dispositivo',
}

interface TrafficSourceTimelineProps {
  storeId: string
  windowDays?: number
  initialData?: ProductsOriginsTimelineResponse | null
}

interface SeriesDef { key: string; label: string; color: string }

// GA4-style channels (keys match the pipeline's categorizeChannel + GA4 names).
// 'unattributed' is intentionally excluded — it's not a real channel.
const CHANNEL_DEFS: SeriesDef[] = [
  { key: 'Paid Search', label: 'Búsqueda pagada', color: '#1e3a8a' },
  { key: 'Paid Social', label: 'Social pagado', color: '#8b5cf6' },
  { key: 'Organic Search', label: 'Búsqueda orgánica', color: '#3b82f6' },
  { key: 'Organic Social', label: 'Social orgánico', color: '#ec4899' },
  { key: 'Email', label: 'Email', color: '#0d9488' },
  { key: 'Referral', label: 'Referidos', color: '#94a3b8' },
  { key: 'Paid Other', label: 'Otros pagados', color: '#dc2626' },
  { key: 'Direct', label: 'Directo', color: '#10b981' },
]

// Source-bucket view (the 9 origin buckets), one finer than channels.
const ORIGIN_DEFS: SeriesDef[] = [
  { key: 'google', label: 'Google (Orgánico)', color: '#3b82f6' },
  { key: 'google_paid', label: 'Google Ads', color: '#1e3a8a' },
  { key: 'facebook', label: 'Facebook', color: '#1d4ed8' },
  { key: 'instagram', label: 'Instagram', color: '#ec4899' },
  { key: 'meta_ads', label: 'Meta Ads', color: '#8b5cf6' },
  { key: 'direct', label: 'Directo', color: '#10b981' },
  { key: 'other_search', label: 'Otros buscadores', color: '#f59e0b' },
  { key: 'other_paid', label: 'Otros pagados', color: '#dc2626' },
  { key: 'other_external', label: 'Otros referidos', color: '#94a3b8' },
]

// Device dimension (mobile/desktop/unknown). `unknown` = no viewport seen (measurement gap).
const DEVICE_DEFS: SeriesDef[] = [
  { key: 'mobile', label: 'Celular', color: '#3b82f6' },
  { key: 'desktop', label: 'Escritorio', color: '#8b5cf6' },
  { key: 'unknown', label: 'Sin dato', color: '#94a3b8' },
]

const CAMPAIGN_COLORS = ['#8b5cf6', '#3b82f6', '#ec4899', '#10b981', '#f59e0b', '#dc2626', '#0d9488', '#6366f1']

export function TrafficSourceTimeline({
  storeId,
  windowDays = 7,
  initialData,
}: TrafficSourceTimelineProps) {
  const [data, setData] = useState<ProductsOriginsTimelineResponse | null>(initialData ?? null)
  const [isLoading, setIsLoading] = useState(!initialData)
  const [error, setError] = useState<string | null>(null)
  const [metric, setMetric] = useState<Metric>('sessions')
  const [smoothing, setSmoothing] = useState<Smoothing>('raw')
  const [dimension, setDimension] = useState<Dimension>('channel')
  const [hidden, setHidden] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (initialData) {
      setData(initialData)
      setIsLoading(false)
      return
    }
    if (!storeId) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    funnelService
      .getStoreProductsOriginsTimeline(storeId, windowDays)
      .then((res) => {
        if (cancelled) return
        setData(res)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load traffic source timeline:', err)
        setError('No se pudo cargar la evolución de orígenes')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [storeId, windowDays, initialData])

  const { chartData, visibleSources } = useMemo(() => {
    const empty = { chartData: [] as Record<string, string | number | null>[], visibleSources: [] as SeriesDef[] }
    if (!data) return empty

    // Active series defs + a per-point cell accessor, by dimension.
    //
    // `engaged` is DELIBERATELY nullable and carries its own denominator: the behaviour
    // counters ramped from their deploy date, so a day that never measured them must produce
    // a GAP in the line (connectNulls={false}), never a 0. Plotting 0 would draw a collapse
    // that never happened — the same failure as «$ 0 por visita» on the merchant surface.
    type Cell = {
      sessions: number
      conversions: number
      revenue: number
      engaged: number | null
      engagedSessions: number
    }
    let defs: SeriesDef[]
    let valueAt: (point: ProductsOriginsTimelinePoint, key: string) => Cell

    if (dimension === 'origin' || dimension === 'device') {
      // These two dimensions carry engagement as ga4-engagement-v1 `segments`, not as an
      // `engaged` field — engaged = everything that isn't a bounce. Same definition, same
      // numbers as the channel cells (a day's meta_ads segments sum to Paid Social `engaged`).
      defs = dimension === 'origin' ? ORIGIN_DEFS : DEVICE_DEFS
      valueAt = (p, k) => {
        // Both dimensions share the cell shape but not the key union, so index one type.
        const bag = (dimension === 'origin' ? p.origins : p.devices) as
          | Record<string, { sessions?: number; conversions?: number; revenue?: number; segments?: CellSegments; segment_sessions?: number }>
          | undefined
        const c = bag?.[k]
        const seg = engagedFromSegments(c)
        return {
          sessions: c?.sessions ?? 0,
          conversions: c?.conversions ?? 0,
          revenue: c?.revenue ?? 0,
          engaged: seg ? seg.engaged : null,
          engagedSessions: seg ? seg.sessions : 0,
        }
      }
    } else if (dimension === 'campaign') {
      // Top campaigns across the window (from the window aggregate), plotted per day.
      const totals = new Map<string, number>()
      for (const cell of Object.values(data.traffic_origins_breakdown || {})) {
        for (const c of cell.campaigns || []) totals.set(c.campaign, (totals.get(c.campaign) || 0) + (c.sessions || 0))
      }
      defs = [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, CAMPAIGN_COLORS.length)
        .map(([name], i) => ({ key: name, label: name, color: CAMPAIGN_COLORS[i] }))
      valueAt = (p, k) => {
        let sessions = 0, conversions = 0, revenue = 0
        let engaged: number | null = null
        let engagedSessions = 0
        for (const cell of Object.values(p.breakdown || {})) {
          const m = (cell.campaigns || []).find((c) => c.campaign === k)
          if (!m) continue
          sessions += m.sessions || 0
          conversions += m.conversions || 0
          revenue += m.revenue || 0
          if (m.engaged != null) {
            engaged = (engaged ?? 0) + m.engaged
            engagedSessions += m.sessions || 0
          }
        }
        return { sessions, conversions, revenue, engaged, engagedSessions }
      }
    } else {
      defs = CHANNEL_DEFS
      valueAt = (p, k) => {
        const c = p.channels?.[k]
        return {
          sessions: c?.sessions ?? 0,
          conversions: c?.conversions ?? 0,
          revenue: c?.revenue ?? 0,
          engaged: c?.engaged ?? null,
          engagedSessions: c?.engaged != null ? c.sessions ?? 0 : 0,
        }
      }
    }

    type Row = Record<string, string | number | null>
    const points: Row[] = (data.series || []).map((point) => {
      const row: Row = {
        date: point.date,
        // parseISO reads a 'YYYY-MM-DD' day key as LOCAL midnight; 'T00:00:00Z' shifted every label a day early west of UTC
        label: format(parseISO(point.date), 'dd MMM', { locale: es }),
      }
      for (const d of defs) {
        const cell = valueAt(point, d.key)
        const { sessions, conversions, revenue, engaged, engagedSessions } = cell
        if (metric === 'sessions') row[d.key] = sessions
        else if (metric === 'conversions') row[d.key] = conversions
        else if (metric === 'revenue') row[d.key] = Math.round(revenue)
        else if (metric === 'value_per_visit') row[d.key] = sessions > 0 ? +(revenue / sessions).toFixed(2) : null
        else if (metric === 'engagement_rate') {
          // Null, not 0, when the day carried no counters — see the Cell comment above.
          row[d.key] = engaged != null && engagedSessions > 0
            ? +((engaged / engagedSessions) * 100).toFixed(2)
            : null
        } else row[d.key] = sessions > 0 ? +((conversions / sessions) * 100).toFixed(2) : null
      }
      return row
    })

    // 3-day moving average
    let series = points
    if (smoothing === 'ma3' && points.length) {
      const window = 3
      series = points.map((_, i) => {
        const out: Row = { date: points[i].date, label: points[i].label }
        for (const d of defs) {
          let sum = 0
          let count = 0
          for (let k = Math.max(0, i - window + 1); k <= i; k++) {
            const v = points[k][d.key]
            if (typeof v === 'number') { sum += v; count += 1 }
          }
          out[d.key] = count === 0
            ? (NULLABLE_METRICS.has(metric) ? null : 0)
            : +(sum / count).toFixed(2)
        }
        return out
      })
    }

    // Hide series with no signal across the window to keep the chart legible.
    const totals: Record<string, number> = {}
    for (const d of defs) {
      totals[d.key] = series.reduce((sum, row) => sum + (typeof row[d.key] === 'number' ? (row[d.key] as number) : 0), 0)
    }
    const nonZero = defs.filter((d) => totals[d.key] > 0)

    return { chartData: series, visibleSources: nonZero }
  }, [data, metric, smoothing, dimension])

  // Axis ticks stay short (a 62.9M revenue tick would eat the plot); the tooltip spells it out.
  const fmtAxis = (v: number) => {
    if (RATE_METRICS.has(metric)) return `${v}%`
    if (metric === 'revenue') {
      if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
      if (Math.abs(v) >= 1_000) return `$${Math.round(v / 1_000)}k`
      return `$${v}`
    }
    if (metric === 'value_per_visit') return `$${Math.round(v).toLocaleString('es-AR')}`
    return `${v}`
  }
  const fmtTip = (v: number) => {
    if (RATE_METRICS.has(metric)) return `${v}%`
    if (MONEY_METRICS.has(metric)) return `$${Math.round(v).toLocaleString('es-AR')}`
    return v.toLocaleString('es-AR')
  }

  const toggleSource = (key: string) => {
    setHidden((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Icon source={<StatsIcon />} />
            <Title as="h3">Evolución por Canal</Title>
          </div>
          <Text>Sesiones diarias por canal de tráfico</Text>
        </Card.Header>
        <Card.Body>
          <div className="h-[280px] w-full animate-pulse rounded-md bg-muted" />
        </Card.Body>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Evolución por Canal</Title>
        </Card.Header>
        <Card.Body>
          <p className="text-sm text-red-600">{error}</p>
        </Card.Body>
      </Card>
    )
  }

  const header = (
    <Card.Header>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Icon source={<StatsIcon />} />
            <Title as="h3" className="sm:whitespace-nowrap">Evolución por {DIMENSION_LABELS[dimension]}</Title>
          </div>
          <Text>
            {dimension === 'campaign' ? 'Top campañas' : dimension === 'origin' ? 'Orígenes' : 'Canales'} · últimos {windowDays} días
          </Text>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(DIMENSION_LABELS) as Dimension[]).map((d) => (
              <Button
                key={d}
                size="small"
                appearance={dimension === d ? 'primary' : 'transparent'}
                onClick={() => { setDimension(d); setHidden(new Set()) }}
              >
                {DIMENSION_LABELS[d]}
              </Button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(SMOOTHING_LABELS) as Smoothing[]).map((s) => (
              <Button
                key={s}
                size="small"
                appearance={smoothing === s ? 'primary' : 'transparent'}
                onClick={() => setSmoothing(s)}
              >
                {SMOOTHING_LABELS[s]}
              </Button>
            ))}
          </div>
          <div className="flex gap-1 rounded-md border p-0.5">
            {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => (
              <Button
                key={m}
                size="small"
                appearance={metric === m ? 'primary' : 'transparent'}
                onClick={() => setMetric(m)}
              >
                {METRIC_LABELS[m]}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </Card.Header>
  )

  if (!chartData.length || !visibleSources.length) {
    return (
      <Card>
        {header}
        <Card.Body>
          <Text className="py-8 text-center">
            No hay datos suficientes para esta métrica en el rango.
          </Text>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Card>
      {header}
      <Card.Body>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 12 }}
              allowDecimals={RATE_METRICS.has(metric) || MONEY_METRICS.has(metric)}
              tickFormatter={fmtAxis}
              width={MONEY_METRICS.has(metric) ? 64 : undefined}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                const def = visibleSources.find((s) => s.key === name)
                return [fmtTip(value), def?.label || name]
              }}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
              onClick={(entry) => {
                if (entry?.dataKey) toggleSource(String(entry.dataKey))
              }}
              formatter={(value) => {
                const def = visibleSources.find((s) => s.key === value)
                const label = def?.label || value
                const isHidden = hidden.has(String(value))
                return (
                  <span
                    style={{
                      opacity: isHidden ? 0.4 : 1,
                      textDecoration: isHidden ? 'line-through' : 'none',
                    }}
                  >
                    {label}
                  </span>
                )
              }}
            />
            {visibleSources.map((src) => (
              <Line
                key={src.key}
                type="monotone"
                dataKey={src.key}
                stroke={src.color}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                hide={hidden.has(src.key)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card.Body>
    </Card>
  )
}

export default TrafficSourceTimeline
